import { useSwrApi } from '@hooks/useSwrApi/useSwrApi';
import type { ApiSchema } from '@type/api/apiUtilityFn';

type ScreeningItemDto = ApiSchema<'ScreeningItemDto'>;

interface UseSwrScreeningItemsOptions {
  jobSlug: string | null;
  refreshInterval?: number;
  enabled?: boolean;
}

/**
 * Hook to fetch screening items (candidates) for a job
 * Supports polling for real-time status updates
 */
export const useSwrScreeningItems = ({
  jobSlug,
  refreshInterval = 0,
  enabled = true,
}: UseSwrScreeningItemsOptions) => {
  const shouldFetch = enabled && jobSlug;

  const { data, error, isLoading, isValidating, mutate } = useSwrApi<ApiSchema<'ScreeningItemListDto'>>({
    url: `/v1/screening/screenings/${jobSlug}/items`,
    shouldFetch: !!shouldFetch,
    apiOptions: {
      refreshInterval: shouldFetch ? refreshInterval : 0,
      revalidateOnFocus: false,
    },
  });

  // Calculate processing stats
  const items: ScreeningItemDto[] = (data?.items) ?? [];
  const isProcessing = items.some((item) => ['PENDING_PARSING', 'PARSING', 'PENDING_MATCHING', 'MATCHING'].includes(item.status ?? ''));
  const completedCount = items.filter((item) => ['MATCHED', 'MATCH_FAILED', 'INTERVIEW_SCHEDULED', 'INTERVIEW_IN_PROGRESS', 'INTERVIEW_COMPLETED', 'INTERVIEW_FAILED'].includes(item.status ?? '')).length;
  const errorCount = items.filter((item) => ['PARSE_FAILED', 'MATCH_FAILED', 'INTERVIEW_FAILED'].includes(item.status ?? '')).length;

  // Get stats and jobId from backend
  const backendStats = data?.stats ?? undefined;
  const jobId = data?.jobId ?? undefined;

  return {
    data,
    items,
    stats: backendStats,
    jobId,
    error,
    isLoading,
    isValidating,
    isProcessing,
    completedCount,
    errorCount,
    totalCount: items.length,
    mutate,
  };
};

export default useSwrScreeningItems;
